import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UsageData, usageFromResponse, useUsage } from '../../contexts/UsageContext';
import { config } from '../../config/env';
import { useAuthenticatedRequest } from '../../lib/useAuthenticatedRequest';
import { AccountRequestError, accountRequestMessage } from '../../lib/accountRequest';

const ACTIVATION_WAIT_MS = 60_000;
const RECHECK_INTERVAL_MS = 5_000;

type Confirmation = {
  ownerId: string | undefined;
  loading: boolean;
  usage: UsageData | null;
  error: string | null;
  needsSignIn: boolean;
};

export const useConfirmedUsage = () => {
  const { user } = useAuth();
  const { beginUsageConfirmation } = useUsage();
  const request = useAuthenticatedRequest();
  const pending = useRef(false);
  const sequence = useRef(0);
  const cancelWait = useRef<(() => void) | null>(null);
  const [state, setState] = useState<Confirmation>({ ownerId: user?.id, loading: true, usage: null, error: null, needsSignIn: false });
  const refresh = useCallback(async () => {
    if (pending.current) return;
    pending.current = true;
    const current = ++sequence.current;
    const ownerId = user?.id;
    const publishConfirmation = beginUsageConfirmation(ownerId);
    const deadline = Date.now() + ACTIVATION_WAIT_MS;
    setState({ ownerId, loading: true, usage: null, error: null, needsSignIn: false });
    let usage: UsageData | null = null;
    let lastError: string | null = null;
    try {
      while (current === sequence.current && Date.now() < deadline) {
        try {
          const confirmed = await request(async ({ ownerId, accessToken, signal }) => {
            const response = await fetch(config.endpoints.refreshCache(ownerId), {
              method: 'POST', headers: { Authorization: `Bearer ${accessToken}` }, signal,
            });
            if (response.status === 401) throw new AccountRequestError('authentication', 'Please sign in again to check your access.');
            if (!response.ok) throw new Error('Account confirmation unavailable');
            const fresh = usageFromResponse(await response.json(), true);
            if (!fresh) throw new Error('Invalid account confirmation');
            return fresh;
          }, deadline - Date.now());
          if (current !== sequence.current) return;
          if (Date.now() >= deadline) break;
          if (!publishConfirmation(confirmed)) return;
          usage = confirmed;
          lastError = null;
          if (confirmed.subscription_status === 'active') {
            setState({ ownerId, loading: false, usage, error: null, needsSignIn: false });
            return;
          }
        } catch (error) {
          if (current !== sequence.current || (error instanceof AccountRequestError && error.kind === 'canceled')) return;
          lastError = accountRequestMessage(error);
          if (error instanceof AccountRequestError && error.kind === 'authentication') {
            setState({ ownerId, loading: false, usage: null, error: lastError, needsSignIn: true });
            return;
          }
        }
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;
        // Wait only after the previous request settles; owner changes/unmount
        // release this wait without starting another request for the old owner.
        await new Promise<void>(resolve => {
          const timer = window.setTimeout(() => { cancelWait.current = null; resolve(); }, Math.min(RECHECK_INTERVAL_MS, remaining));
          cancelWait.current = () => { window.clearTimeout(timer); resolve(); };
        });
      }
      if (current === sequence.current) {
        setState({ ownerId, loading: false, usage, error: lastError, needsSignIn: false });
      }
    } finally {
      if (current === sequence.current) pending.current = false;
    }
  }, [beginUsageConfirmation, request, user?.id]);

  useEffect(() => {
    pending.current = false;
    void refresh();
    return () => {
      sequence.current += 1;
      cancelWait.current?.();
      cancelWait.current = null;
    };
  }, [refresh]);

  return { ...(state.ownerId === user?.id ? state : { ownerId: user?.id, loading: true, usage: null, error: null, needsSignIn: false }), refresh };
};
